/**
 * Demo data for policy v2 and secrets (?mock=1): settings layers, freeze windows, model
 * switches, a version history built from the demo rules, and secrets with leases. Built on
 * the mock projects and sessions so every row links to something real-looking.
 */
import type { FreezeWindow, Id, ModelSwitch, PolicyChange, PolicyConfig, PolicyRule, SecretLease, SecretMeta, SecretsState, Session } from '@orgus/protocol';
import { normalizeConfig } from '@orgus/policy';

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

export function mockConfig(projectIds: Id[], now: number): PolicyConfig {
  const [a, b] = projectIds;
  const projects: PolicyConfig['projects'] = {};
  if (a) projects[a] = { allowedModels: ['sonnet', 'haiku'], secrets: ['NPM_TOKEN', 'SENTRY_AUTH_TOKEN'], reviewer: 'human-or-agent', permissionMode: 'acceptEdits' };
  if (b) projects[b] = { defaultModel: 'haiku', env: { CI: '1', LOG_LEVEL: 'warn' } };
  const freezes: FreezeWindow[] = [
    { id: 'frz_DEMO_WEEKEND', name: 'Friday evening', scope: { kind: 'org' }, kind: 'weekly', days: [5], start: '18:00', end: '23:59', utcOffsetMinutes: -new Date(now).getTimezoneOffset(), blocks: ['landing', 'deploy'], reason: 'Nobody is around to watch a deploy.', createdBy: 'owner', createdAt: now - 20 * DAY },
    ...(a
      ? [{ id: 'frz_DEMO_RELEASE', name: 'Release candidate', scope: { kind: 'project', projectId: a }, kind: 'once', from: now - 30 * MIN, to: now + 5 * HOUR, blocks: ['landing'], reason: 'v2.4 is in final QA.', createdBy: 'owner', createdAt: now - 2 * HOUR } satisfies FreezeWindow]
      : []),
  ];
  return {
    org: { defaultModel: 'sonnet', allowedModels: ['sonnet', 'haiku', 'opus'], secrets: ['NPM_TOKEN'], requiredChecks: ['tests', 'lint'], disableBypassPermissions: true },
    projects,
    freezes,
  };
}

export function mockModelSwitches(sessions: Session[], now: number): ModelSwitch[] {
  const s = sessions.filter((x) => x.status !== 'ended');
  const pick = (i: number) => s[i % Math.max(1, s.length)];
  const a = pick(0);
  const b = pick(1);
  if (!a || !b) return [];
  return [
    { at: now - 6 * MIN, sessionId: a.id, machineId: a.machineId, projectId: a.projectId, from: 'claude-sonnet-4-5', to: 'claude-opus-5', allowed: false, launched: true, reason: 'Not in the allowed models (sonnet, haiku).' },
    { at: now - 52 * MIN, sessionId: b.id, machineId: b.machineId, projectId: b.projectId, from: 'claude-sonnet-4-5', to: 'claude-haiku-4-5', allowed: true, launched: false },
  ];
}

/** A change log that replays to the demo rules and settings, newest last. */
export function mockChanges(rules: PolicyRule[], config: PolicyConfig, now: number): PolicyChange[] {
  const out: PolicyChange[] = [];
  let v = 0;
  const push = (c: Omit<PolicyChange, 'version'>) => out.push({ ...c, version: ++v });
  const sorted = [...rules].sort((x, y) => x.createdAt - y.createdAt);
  const empty = normalizeConfig(null);
  const orgOnly: PolicyConfig = { ...empty, org: { defaultModel: 'opus', allowedModels: ['sonnet', 'haiku', 'opus'] } };
  for (const r of sorted.slice(0, 2)) push({ at: r.createdAt, by: r.createdBy, kind: 'create', ruleId: r.id, after: { ...r, decision: r.id === 'pol_DEMO_PUSH' ? 'allow' : r.decision } });
  push({ at: now - 19 * DAY, by: 'owner', kind: 'settings', ruleId: '', configBefore: empty, configAfter: orgOnly });
  const push2 = sorted.find((r) => r.id === 'pol_DEMO_PUSH');
  if (push2) push({ at: now - 15 * DAY, by: 'maya', kind: 'update', ruleId: push2.id, before: { ...push2, decision: 'allow' }, after: push2 });
  for (const r of sorted.slice(2)) if (!out.some((c) => c.ruleId === r.id)) push({ at: r.createdAt, by: r.createdBy, kind: 'create', ruleId: r.id, after: r });
  push({ at: now - 3 * DAY, by: 'maya', kind: 'settings', ruleId: '', configBefore: orgOnly, configAfter: { ...config, freezes: [] } });
  push({ at: now - 2 * HOUR, by: 'owner', kind: 'freeze', ruleId: '', configBefore: { ...config, freezes: [] }, configAfter: config });
  return out.sort((x, y) => x.at - y.at).map((c, i) => ({ ...c, version: i + 1 }));
}

export function mockSecrets(projectIds: Id[], machineIds: Id[], sessions: Session[], now: number): SecretsState {
  const p = projectIds[0] ?? null;
  const m = machineIds[0] ?? 'demo-machine';
  const secrets: SecretMeta[] = [
    { id: 'sec_DEMO_NPM', name: 'NPM_TOKEN', description: 'Read-only token for the private registry', scope: { kind: 'org' }, createdBy: 'owner', createdAt: now - 30 * DAY, valueVersion: 3, updatedBy: 'maya', updatedAt: now - 4 * DAY, lastLeasedAt: now - 8 * MIN, leaseCount: 41 },
    ...(p
      ? [{ id: 'sec_DEMO_SENTRY', name: 'SENTRY_AUTH_TOKEN', description: 'Uploads source maps', scope: { kind: 'project', projectId: p }, createdBy: 'owner', createdAt: now - 12 * DAY, valueVersion: 1, lastLeasedAt: now - 8 * MIN, leaseCount: 9 } satisfies SecretMeta]
      : []),
    { id: 'sec_DEMO_STRIPE', name: 'STRIPE_TEST_KEY', scope: { kind: 'org' }, createdBy: 'maya', createdAt: now - 2 * DAY, valueVersion: 1, leaseCount: 0 },
  ];
  const live = sessions.find((s) => s.status !== 'ended' && s.origin === 'orgus') ?? sessions[0];
  const leases: SecretLease[] = [
    { id: 'lse_DEMO_1', names: ['NPM_TOKEN', ...(p ? ['SENTRY_AUTH_TOKEN'] : [])], secretIds: ['sec_DEMO_NPM'], machineId: live?.machineId ?? m, runId: 'run-demo-1', projectId: p, sessionId: live?.id ?? null, createdAt: now - 8 * MIN, expiresAt: now + 12 * HOUR - 8 * MIN, state: 'active' },
    { id: 'lse_DEMO_2', names: ['NPM_TOKEN'], secretIds: ['sec_DEMO_NPM'], machineId: m, runId: 'run-demo-2', projectId: p, sessionId: sessions[1]?.id ?? null, createdAt: now - 3 * HOUR, expiresAt: now + 9 * HOUR, state: 'revoked', endedAt: now - 2 * HOUR, endReason: 'The session ended.' },
    { id: 'lse_DEMO_3', names: ['NPM_TOKEN'], secretIds: ['sec_DEMO_NPM'], machineId: m, runId: 'run-demo-3', projectId: null, sessionId: null, createdAt: now - 26 * HOUR, expiresAt: now - 14 * HOUR, state: 'expired', endedAt: now - 14 * HOUR, endReason: 'Expired after 12 hours.' },
  ];
  return { secrets, leases, keyring: { masterKeySource: 'keystore', keystore: 'Windows Credential Manager', masterKeyId: '3fa91c07', dataKeyCreatedAt: now - 30 * DAY } };
}
